import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  QueryConstraint,
} from 'firebase/firestore';
import { firestore } from './client';
import type {
  Growler,
  GrowlerFilterOptions,
} from '../../utils/types/Growler';

export class GrowlerService {
  private static readonly GROWLERS_COLLECTION = 'growlers';
  private static readonly CATEGORIES_COLLECTION = 'growlerCategories';

  static async getAllGrowlers(): Promise<Growler[]> {
    try {
      const growlersRef = collection(firestore, this.GROWLERS_COLLECTION);
      const snapshot = await getDocs(growlersRef);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as Growler[];
    } catch (error) {
      console.error('Erro ao buscar growlers:', error);
      throw new Error('Falha ao carregar growlers');
    }
  }

  static async getGrowlerById(id: string): Promise<Growler | null> {
    try {
      const growlerRef = doc(firestore, this.GROWLERS_COLLECTION, id);
      const snapshot = await getDoc(growlerRef);

      if (snapshot.exists()) {
        return {
          id: snapshot.id,
          ...snapshot.data(),
        } as Growler;
      }

      return null;
    } catch (error) {
      console.error('Erro ao buscar growler por ID:', error);
      throw new Error('Falha ao carregar growler');
    }
  }

  static async getFilteredGrowlers(filters: {
    breweries?: string[];
    styles?: string[];
    inStock?: boolean;
    featured?: boolean;
    limitCount?: number;
  }): Promise<Growler[]> {
    try {
      const growlersRef = collection(firestore, this.GROWLERS_COLLECTION);
      const constraints: QueryConstraint[] = [];

      if (filters.inStock !== undefined) {
        constraints.push(where('inStock', '==', filters.inStock));
      }

      if (filters.featured !== undefined) {
        constraints.push(where('featured', '==', filters.featured));
      }

      if (filters.breweries && filters.breweries.length > 0) {
        constraints.push(where('brewery', 'in', filters.breweries));
      }

      if (filters.styles && filters.styles.length > 0) {
        constraints.push(where('style', 'in', filters.styles));
      }

      constraints.push(orderBy('name', 'asc'));

      if (filters.limitCount) {
        constraints.push(limit(filters.limitCount));
      }

      const q = query(growlersRef, ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as Growler[];
    } catch (error) {
      console.error('Erro ao filtrar growlers:', error);
      throw new Error('Falha ao filtrar growlers');
    }
  }

  static async getFilterOptions(): Promise<GrowlerFilterOptions> {
    try {
      const categoriesRef = collection(
        firestore,
        this.CATEGORIES_COLLECTION,
      );
      const categoriesSnapshot = await getDocs(categoriesRef);

      if (!categoriesSnapshot.empty) {
        const breweries =
          categoriesSnapshot.docs.find(docSnap => docSnap.id === 'breweries')
            ?.data()?.list || [];
        const styles =
          categoriesSnapshot.docs.find(docSnap => docSnap.id === 'styles')
            ?.data()?.list || [];

        const growlers = await this.getAllGrowlers();
        const prices = growlers.map(item => item.price);
        const priceRange = {
          min: prices.length ? Math.min(...prices) : 0,
          max: prices.length ? Math.max(...prices) : 50,
        };

        return {
          breweries,
          styles,
          priceRange,
        };
      }

      const growlers = await this.getAllGrowlers();
      const breweries = [
        ...new Set(growlers.map(item => item.brewery)),
      ].sort();
      const styles = [...new Set(growlers.map(item => item.style))].sort();
      const prices = growlers.map(item => item.price);

      return {
        breweries,
        styles,
        priceRange: {
          min: prices.length ? Math.min(...prices) : 0,
          max: prices.length ? Math.max(...prices) : 50,
        },
      };
    } catch (error) {
      console.error('Erro ao buscar opções de filtro de growlers:', error);
      return {
        breweries: [],
        styles: [],
        priceRange: { min: 0, max: 50 },
      };
    }
  }
}
